/**
 * Per-portal-user preferences: which dashboard sections are hidden, and in what
 * order they appear.
 *
 * `user_preferences` shipped as schema with no UI. This is that UI's data layer.
 *
 * Two rules it follows. Staff viewing as a customer are read-only, because a
 * layout saved while impersonating would be stored under the customer's name.
 * And a write never blocks the render: the mutation updates the cache first, so
 * dragging a section feels instant and a failed save restores the previous
 * order rather than leaving the page lying about what was kept.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useViewAs } from '../contexts/ViewAsContext';
import {
  DEFAULT_LAYOUT,
  moveWidget,
  normalizeLayout,
  toStoredLayout,
  type DashboardLayout,
  type DashboardWidget,
} from '../lib/dashboardLayout';

export {
  DASHBOARD_WIDGETS,
  DEFAULT_LAYOUT,
  moveWidget,
  normalizeLayout,
  toStoredLayout,
  type DashboardLayout,
  type DashboardWidget,
} from '../lib/dashboardLayout';

export interface UserPreferencesResult {
  layout: DashboardLayout;
  isLoading: boolean;
  readOnly: boolean;
  /** Widgets to render, in order, with hidden ones removed. */
  visibleWidgets: DashboardWidget[];
  isHidden: (widget: DashboardWidget) => boolean;
  toggleWidget: (widget: DashboardWidget) => void;
  moveUp: (widget: DashboardWidget) => void;
  moveDown: (widget: DashboardWidget) => void;
  reset: () => void;
}

export function useUserPreferences(): UserPreferencesResult {
  const { isStaff, viewAsUserId } = useViewAs();
  const readOnly = isStaff && !!viewAsUserId;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['user-preferences'],
    queryFn: async (): Promise<DashboardLayout> => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('pinned_widgets, saved_views')
        .maybeSingle();
      // A user with no row yet is the normal first-visit case, not an error.
      if (error && error.code !== 'PGRST116') throw error;
      return normalizeLayout(data?.pinned_widgets);
    },
    staleTime: 5 * 60 * 1000,
  });

  const layout = query.data ?? DEFAULT_LAYOUT;

  const save = useMutation({
    mutationFn: async (next: DashboardLayout) => {
      if (readOnly) return;
      const { data: userId, error: whoError } = await supabase.rpc('get_my_user_id');
      if (whoError) throw whoError;
      if (!userId) throw new Error('no_profile');
      const { error } = await supabase.from('user_preferences').upsert(
        {
          user_id: userId,
          // An array of { id, visible }: the column carries a check constraint
          // requiring a JSON array.
          pinned_widgets: toStoredLayout(next).map((entry) => ({ ...entry })),
        },
        { onConflict: 'user_id' },
      );
      if (error) throw error;
    },
    onMutate: async (next) => {
      // Optimistic: the control should feel immediate.
      await queryClient.cancelQueries({ queryKey: ['user-preferences'] });
      const previous = queryClient.getQueryData<DashboardLayout>(['user-preferences']);
      queryClient.setQueryData(['user-preferences'], next);
      return { previous };
    },
    onError: (_error, _next, context) => {
      // Put the old layout back rather than leaving the page claiming a save.
      if (context?.previous) queryClient.setQueryData(['user-preferences'], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });

  const apply = useCallback(
    (next: DashboardLayout) => {
      if (readOnly) return;
      save.mutate(next);
    },
    [readOnly, save],
  );

  const visibleWidgets = useMemo(
    () => layout.order.filter((widget) => !layout.hidden.includes(widget)),
    [layout],
  );

  return {
    layout,
    isLoading: query.isLoading,
    readOnly,
    visibleWidgets,
    isHidden: (widget) => layout.hidden.includes(widget),
    toggleWidget: (widget) =>
      apply({
        ...layout,
        hidden: layout.hidden.includes(widget)
          ? layout.hidden.filter((item) => item !== widget)
          : [...layout.hidden, widget],
      }),
    moveUp: (widget) => apply({ ...layout, order: moveWidget(layout.order, widget, -1) }),
    moveDown: (widget) => apply({ ...layout, order: moveWidget(layout.order, widget, 1) }),
    reset: () => apply(DEFAULT_LAYOUT),
  };
}
